import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { apiAIFetch } from '../../api';
import EquationBuilder from './EquationBuilder';
import { normalizeGraphExpressions, parseGraphExpressions, DEFAULT_GRAPH_EXPRESSION } from './graphExpressions';

// Color palette for graph expressions
const GRAPH_COLORS = ['#1F93FE', '#F7521B', '#2ECC71', '#9B59B6', '#E74C3C', '#3498DB'];

// Quick preset functions
const GRAPH_PRESETS = [
  { label: 'y=x', value: 'y=x' },
  { label: 'y=x²', value: 'y=x^2' },
  { label: 'y=-x²', value: 'y=-x^2' },
  { label: 'sin(x)', value: 'y=sin(x)' },
  { label: 'cos(x)', value: 'y=cos(x)' },
  { label: 'tan(x)', value: 'y=tan(x)' },
  { label: 'circle', value: 'x^2+y^2=16' },
  { label: 'e^x', value: 'y=e^x' },
  { label: 'ln(x)', value: 'y=ln(x)' },
  { label: '1/x', value: 'y=1/x' },
  { label: '|x|', value: 'y=|x|' },
  { label: 'x³', value: 'y=x^3' },
];

let desmosScriptPromise = null;

const loadDesmosScript = () => {
  if (window.Desmos) return Promise.resolve(window.Desmos);
  if (desmosScriptPromise) return desmosScriptPromise;
  desmosScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-lrbrain-desmos="true"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.Desmos), { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://www.desmos.com/api/v1.9/calculator.js?apiKey=dcb31709b452b1cf9dc26972add0fda6';
    script.async = true;
    script.dataset.lrbrainDesmos = 'true';
    script.onload = () => resolve(window.Desmos);
    script.onerror = reject;
    document.body.appendChild(script);
  });
  return desmosScriptPromise;
};

const GraphComponent = ({ expressions, isSelected = false, onUpdate }) => {
  const [showEditor, setShowEditor] = useState(false);
  const [localExpressions, setLocalExpressions] = useState(
    normalizeGraphExpressions(expressions, DEFAULT_GRAPH_EXPRESSION)
  );
  const [showBuilder, setShowBuilder] = useState(false);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const containerRef = useRef(null);
  const calculatorRef = useRef(null);
  const inputRef = useRef(null);
  const tapStartedRef = useRef(false);

  const isTouchPrimaryPointer = useCallback((event) => {
    if (!event) return false;
    if (event.pointerType === 'touch' || event.pointerType === 'pen') return true;
    if (typeof window !== 'undefined' && window.matchMedia?.('(hover: none) and (pointer: coarse)').matches) {
      return true;
    }
    return false;
  }, []);

  // Initialize Desmos calculator
  useEffect(() => {
    let cancelled = false;
    const initCalculator = () => {
      if (cancelled || !containerRef.current || !window.Desmos) return;

      if (calculatorRef.current) {
        calculatorRef.current.destroy();
      }

      const calculator = window.Desmos.GraphingCalculator(containerRef.current, {
        expressions: true,
        settingsMenu: false,
        zoomButtons: true,
        expressionsTopbar: false,
        expressionsCollapsed: true,
        lockViewport: false,
        border: false,
        keypad: false,
      });

      // Add expressions
      const expList = parseGraphExpressions(localExpressions);
      expList.forEach((exp, i) => {
        calculator.setExpression({
          id: `exp-${i}`,
          latex: exp,
          color: GRAPH_COLORS[i % GRAPH_COLORS.length],
        });
      });

      calculatorRef.current = calculator;
    };

    loadDesmosScript().then(initCalculator).catch((error) => {
      console.error('Failed to load Desmos API', error);
    });

    return () => {
      cancelled = true;
      if (calculatorRef.current) {
        calculatorRef.current.destroy();
        calculatorRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Update expressions when they change
  useEffect(() => {
    if (!calculatorRef.current) return;

    // Clear existing expressions
    try {
      const state = calculatorRef.current.getState();
      state.expressions.list.forEach(exp => {
        if (exp.id) calculatorRef.current.removeExpression({ id: exp.id });
      });
    } catch (e) {
      // Ignore errors when clearing
    }

    // Add new expressions
    const expList = parseGraphExpressions(localExpressions);
    expList.forEach((exp, i) => {
      calculatorRef.current.setExpression({
        id: `exp-${i}`,
        latex: exp,
        color: GRAPH_COLORS[i % GRAPH_COLORS.length],
      });
    });
  }, [localExpressions]);

  const handleDoubleClick = useCallback((e) => {
    e.stopPropagation();
    setShowEditor(true);
  }, []);

  const handleGraphPointerDown = useCallback((e) => {
    e.stopPropagation();
    tapStartedRef.current = true;
  }, []);

  const handleGraphPointerUp = useCallback((e) => {
    e.stopPropagation();
    if (!tapStartedRef.current) return;
    tapStartedRef.current = false;
    if (!isTouchPrimaryPointer(e)) return;
    if (!isSelected) return;
    setShowEditor(true);
  }, [isSelected, isTouchPrimaryPointer]);

  const handleEditorClose = useCallback(() => {
    setShowEditor(false);
    onUpdate({ expressions: normalizeGraphExpressions(localExpressions, DEFAULT_GRAPH_EXPRESSION) });
  }, [localExpressions, onUpdate]);

  const stopEvent = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      handleEditorClose();
    }
  }, [handleEditorClose]);

  useEffect(() => {
    if (showEditor && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showEditor]);

  // Sync local state when prop changes
  useEffect(() => {
    setLocalExpressions(normalizeGraphExpressions(expressions, DEFAULT_GRAPH_EXPRESSION));
  }, [expressions]);

  // Add preset to expressions
  const handlePresetClick = (preset) => {
    setLocalExpressions(prev => {
      const trimmed = prev.trim();
      if (!trimmed) return preset;
      return trimmed + '\n' + preset;
    });
  };

  // Insert equation from builder
  const handleBuilderInsert = (equation) => {
    setLocalExpressions(prev => {
      const trimmed = prev.trim();
      if (!trimmed) return equation;
      return trimmed + '\n' + equation;
    });
    setShowBuilder(false);
  };

  // AI-powered equation generation
  const handleAiMagic = async () => {
    if (!aiInput.trim()) return;

    setAiLoading(true);
    try {
      const response = await apiAIFetch('/api/format-latex', 'whiteboard.format_latex', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: `Convert this to a mathematical function expression for graphing (just the expression, no explanation): ${aiInput}`,
          type: 'graph'
        })
      });

      if (response.ok) {
        const data = await response.json();
        let result = data.latex || aiInput;
        // Clean up the result - remove any LaTeX formatting that Desmos doesn't need
        result = result.replace(/\\left/g, '').replace(/\\right/g, '');
        result = result.replace(/\\,/g, '');

        setLocalExpressions(prev => {
          const trimmed = prev.trim();
          if (!trimmed) return result;
          return trimmed + '\n' + result;
        });
        setAiInput('');
      }
    } catch (error) {
      console.error('Error with AI graph formatting:', error);
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="wb-graph">
      {/* Graph container */}
      <div
        ref={containerRef}
        className="wb-graph-container"
        style={{ height: showEditor ? '60%' : '100%' }}
        onDoubleClick={handleDoubleClick}
        onPointerDown={handleGraphPointerDown}
        onPointerUp={handleGraphPointerUp}
      />

      {/* Expression editor */}
      {showEditor && (
        <div
          className="wb-graph-editor"
          onPointerDown={stopEvent}
          onPointerUp={stopEvent}
          onClick={stopEvent}
        >
          <div className="wb-graph-editor-header">
            <span>Expressions (one per line)</span>
            <button onPointerDown={stopEvent} onClick={handleEditorClose} className="wb-graph-close">
              X
            </button>
          </div>

          {/* Quick preset buttons */}
          <div className="wb-graph-presets">
            {GRAPH_PRESETS.map((preset, index) => (
              <button
                key={index}
                className="wb-graph-preset-btn"
                onClick={() => handlePresetClick(preset.value)}
                onPointerDown={stopEvent}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {/* Equation Builder toggle */}
          <button
            className="wb-templates-btn"
            onClick={() => setShowBuilder(!showBuilder)}
            onPointerDown={stopEvent}
            style={{ marginBottom: '8px', width: '100%', justifyContent: 'center' }}
          >
            {showBuilder ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            Equation Builder
          </button>

          {showBuilder && (
            <EquationBuilder onInsert={handleBuilderInsert} />
          )}

          {/* AI Magic input */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
            <input
              type="text"
              value={aiInput}
              onChange={(e) => setAiInput(e.target.value)}
              placeholder="Describe a function (e.g., 'parabola opening down')"
              className="wb-graph-input"
              style={{ flex: 1, height: 'auto', minHeight: 'auto', padding: '6px 8px' }}
              onPointerDown={stopEvent}
              onClick={stopEvent}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') handleAiMagic();
              }}
            />
            <button
              className="wb-graph-magic-btn"
              onClick={handleAiMagic}
              disabled={aiLoading || !aiInput.trim()}
              onPointerDown={stopEvent}
            >
              {aiLoading ? (
                <span className="wb-spinner" />
              ) : (
                <>
                  <Sparkles size={14} />
                  Magic
                </>
              )}
            </button>
          </div>

          <textarea
            ref={inputRef}
            value={localExpressions}
            onChange={(e) => setLocalExpressions(e.target.value)}
            onKeyDown={handleKeyDown}
            className="wb-graph-input"
            placeholder="Enter expressions like:
y=x^2
y=sin(x)
y=2x+1"
            onPointerDown={stopEvent}
            onClick={stopEvent}
          />
          <div className="wb-graph-hint">
            Examples: y=x^2, y=sin(x), x^2+y^2=25
          </div>
        </div>
      )}

      {/* Hint overlay when not editing */}
      {!showEditor && (
        <div className="wb-graph-hint-overlay">
          Double-click to edit
        </div>
      )}
    </div>
  );
};

export default React.memo(GraphComponent);


